
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ExternalLink, CheckCircle, AlertTriangle, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SentryStatusCardProps {
  sentryStatus: 'checking' | 'configured' | 'not-configured';
}

const SentryStatusCard: React.FC<SentryStatusCardProps> = ({ sentryStatus }) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  const hardcodedDsn = "https://8a37ec87ced3d16acde3b10cbd32e8b@o4509500403351552.ingest.us.sentry.io/4509500564570112";
  const isConfigured = sentryStatus === 'configured';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: "Copied!",
      description: "Command copied to clipboard",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {sentryStatus === 'checking' ? (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          ) : isConfigured ? (
            <CheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-sky-500" />
          )}
          Sentry Error Monitoring Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {sentryStatus === 'checking' ? (
          <Alert>
            <AlertDescription>
              Checking Sentry configuration status...
            </AlertDescription>
          </Alert>
        ) : isConfigured ? (
          <div className="space-y-4">
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Sentry is properly configured and actively monitoring errors in your application.
              </AlertDescription>
            </Alert>
            
            <div className="bg-white border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-800 mb-2">Active Monitoring Features:</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Automatic error capture and reporting</li>
                <li>• Performance monitoring and tracing</li>
                <li>• Session replay for error debugging</li>
                <li>• React error boundary integration</li>
                <li>• Custom error reporting via useErrorReporting hook</li>
              </ul>
            </div>

            <div className="bg-white border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-black mb-2">Current DSN:</h4>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-hidden">
                  {hardcodedDsn}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(hardcodedDsn)}
                  className="flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>

            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://sentry.io/organizations/wisefools/', '_blank')}
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                View Sentry Dashboard
              </Button>
            </div>
          </div>
        ) : (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Sentry client could not be initialized. Please check the configuration.
            </AlertDescription>
          </Alert>
        )}

        <div className="pt-4 border-t">
          <h4 className="font-medium mb-2">Current Status:</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Environment:</span> {import.meta.env.MODE}
            </div>
            <div>
              <span className="font-medium">Sentry Status:</span>{' '}
              {sentryStatus === 'checking' ? (
                <span className="text-black">Checking...</span>
              ) : isConfigured ? (
                <span className="text-green-600 font-medium">✓ Configured</span>
              ) : (
                <span className="text-black font-medium">⚠ Not Configured</span>
              )}
            </div>
            <div>
              <span className="font-medium">Auth Token:</span>{' '}
              <span className="text-green-600">✓ Configured in vite.config.ts</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SentryStatusCard;
